import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

const Section = ({ n, title, children }) => (
  <section className="mt-10">
    <h2 className="text-xl font-semibold text-white tracking-tight mb-3">
      <span className="text-[#00ff88] font-mono mr-2">{n}.</span>
      {title}
    </h2>
    <div className="text-[15px] leading-relaxed text-[#c8c8d0] space-y-3">{children}</div>
  </section>
);

export default function Terms() {
  usePageMeta({
    title: "Terms of Service — CheatCraft",
    description: "The Terms of Service governing use of the CheatCraft marketplace — orders, escrow, watermarks, disputes and refunds.",
    canonicalPath: "/terms",
  });
  return (
    <div data-testid="terms-page" className="min-h-[80vh] px-6 py-16">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#a0a0b0] hover:text-white mb-8"
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="cc-overline">Legal</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mt-2 mb-2">
          Terms of Service
        </h1>
        <p className="text-sm text-[#606070] font-mono">Last updated: May 2026</p>

        <div className="mt-10">
          <Section n="1" title="Introduction">
            <p>
              Welcome to CheatCraft.net (&ldquo;CheatCraft&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). By accessing or
              using our platform, you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do not agree
              to these Terms, do not use CheatCraft.
            </p>
            <p>
              CheatCraft is a marketplace platform that connects gaming software providers (&ldquo;Companies&rdquo;) with
              media content creators (&ldquo;Creators&rdquo;). We act solely as an intermediary and are not a party to any
              transaction between Companies and Creators.
            </p>
          </Section>

          <Section n="2" title="Eligibility">
            <p>
              You must be at least 18 years old to use CheatCraft. By creating an account, you confirm that you are at
              least 18 years of age and have the legal capacity to enter into these Terms.
            </p>
          </Section>

          <Section n="3" title="Account Registration">
            <p>
              You must provide accurate and complete information when creating your account. You are responsible for
              maintaining the confidentiality of your login credentials. You are solely responsible for all activities
              that occur under your account. You must notify us immediately of any unauthorized use of your account. We
              reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section n="4" title="Platform Role">
            <p>
              CheatCraft is an intermediary platform only. We do not create, sell, distribute, or endorse any software,
              product, or content shared between Companies and Creators. We do not verify the legality, functionality,
              or quality of any software shared on the platform. Users are solely responsible for ensuring their
              activities comply with applicable laws in their jurisdiction.
            </p>
          </Section>

          <Section n="5" title="Orders & Payments">
            <p>
              Companies pay upfront when placing an order. All payments are held in escrow until the Company approves
              the delivered content. Creators receive payment only after Company approval. If a Company does not respond
              within 7 days of delivery, funds are automatically released to the Creator. CheatCraft charges a 10%
              service fee on each completed transaction. All prices are displayed in USD. Cryptocurrency payments (LTC)
              are also accepted and are subject to the same terms. Fees are non-negotiable and are deducted automatically
              from each transaction.
            </p>
          </Section>

          <Section n="6" title="Escrow & Watermark System">
            <p>
              All delivered content is automatically watermarked by CheatCraft before being shown to the Company. The
              watermark is removed only upon Company approval. The escrow system ensures that funds are held securely
              until the Company is satisfied with the delivery. Companies may request up to 2 free revisions. Additional
              revisions may incur extra fees as set by the Creator.
            </p>
          </Section>

          <Section n="7" title="Refunds & Disputes">
            <p>
              If a Company is not satisfied after 2 revisions, they may open a dispute. CheatCraft will review the
              original order requirements, the delivered content, and communication history to make a fair decision.
              Refunds are issued at CheatCraft&rsquo;s sole discretion. Fraudulent chargeback attempts will result in
              immediate and permanent account termination and may be reported to relevant authorities.
            </p>
          </Section>

          <Section n="8" title="Creator Obligations">
            <p>
              Creators must deliver content that matches the order requirements. Creators must deliver within the agreed
              deadline. Creators must not share Company software, license keys, or confidential information with third
              parties. Creators set their own prices and are responsible for declaring their income in accordance with
              local tax laws.
            </p>
          </Section>

          <Section n="9" title="Company Obligations">
            <p>
              Companies must provide clear and complete order requirements. Companies must provide functional software
              access (link, file, or license key) for the Creator to produce content. Companies are solely responsible
              for the software they distribute to Creators. Companies must review and respond to deliveries within 7
              days.
            </p>
          </Section>

          <Section n="10" title="Prohibited Conduct">
            <p>The following activities are strictly prohibited on CheatCraft:</p>
            <p>
              Sharing external contact information (Discord, Telegram, email, phone) to circumvent CheatCraft&rsquo;s
              platform and avoid fees. Creating multiple accounts. Filing fraudulent chargebacks or disputes. Uploading
              malicious files (RATs, stealers, trojans, backdoors) disguised as software. Harassing, threatening, or
              abusing other users. Using the platform for any activity that violates applicable laws. Attempting to
              access another user&rsquo;s account. Scraping, reverse-engineering, or interfering with the platform.
            </p>
            <p>
              Violation of these terms may result in immediate account suspension or permanent ban, forfeiture of
              pending balances, and reporting to relevant authorities where applicable.
            </p>
          </Section>

          <Section n="11" title="Files & Safety">
            <p>
              CheatCraft scans uploaded files using third-party security tools including VirusTotal. However, we cannot
              guarantee that all files are safe. Users download and execute files at their own risk. We strongly
              recommend using a virtual machine when testing any software. CheatCraft is not responsible for any damage,
              data loss, or security breach caused by files shared on the platform.
            </p>
          </Section>

          <Section n="12" title="Intellectual Property">
            <p>
              Creators retain ownership of the content they produce. Upon order completion and payment release, the
              Company receives a non-exclusive license to use the delivered content for promotional purposes. CheatCraft
              retains the right to use anonymized transaction data for platform analytics and improvement.
            </p>
          </Section>

          <Section n="13" title="Fees & Withdrawals">
            <p>
              CheatCraft charges a 10% fee on all completed transactions, including add-ons/upsells. Creators can
              withdraw their available balance via bank transfer or cryptocurrency (LTC). Minimum withdrawal amount is
              $20. Withdrawal processing times vary: bank transfers typically take 2&ndash;5 business days,
              cryptocurrency withdrawals are processed within 24 hours. CheatCraft is not responsible for delays caused
              by banks or blockchain networks. A payout delay of 14 days applies after order approval before funds
              become withdrawable, to protect against fraudulent chargebacks.
            </p>
          </Section>

          <Section n="14" title="External Communication Policy">
            <p>
              All communication between Companies and Creators must remain on CheatCraft. Attempting to move
              communication off-platform (via Discord, Telegram, email, or any external channel) to avoid platform fees
              is a violation of these Terms. Accounts found circumventing the platform may be suspended and pending
              balances forfeited.
            </p>
          </Section>

          <Section n="15" title="Limitation of Liability">
            <p>
              CheatCraft is provided &ldquo;as is&rdquo; without warranties of any kind. We are not liable for any loss
              of revenue, data, or profits arising from the use of our platform. We are not liable for any damages
              resulting from software shared between users. We are not responsible for the actions, content, or conduct
              of any user. Our total liability shall not exceed the amount of fees collected from you in the 12 months
              preceding any claim.
            </p>
          </Section>

          <Section n="16" title="Modifications">
            <p>
              We reserve the right to modify these Terms at any time. Users will be notified of significant changes via
              email or platform notification. Continued use of the platform after changes constitutes acceptance of the
              revised Terms.
            </p>
          </Section>

          <Section n="17" title="Termination">
            <p>
              We may suspend or terminate your account at any time for violation of these Terms. Upon termination, any
              pending balances may be forfeited if the termination is due to fraudulent or prohibited activity. You may
              delete your account at any time through your account settings.
            </p>
          </Section>

          <Section n="18" title="Governing Law">
            <p>
              These Terms are governed by and construed in accordance with the laws of France. Any disputes arising
              from these Terms shall be subject to the exclusive jurisdiction of the courts of France.
            </p>
          </Section>

          <Section n="19" title="Contact">
            <p>
              For questions about these Terms, contact us at:{" "}
              <a
                data-testid="terms-contact-email"
                className="text-[#00ff88] hover:underline font-mono"
                href="mailto:support@cheatcraft.net"
              >
                support@cheatcraft.net
              </a>
              .
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-6 border-t border-[#15152a] flex items-center justify-between text-xs text-[#606070]">
          <Link to="/privacy" data-testid="terms-to-privacy" className="hover:text-white">
            Privacy Policy →
          </Link>
          <span className="font-mono">CHEATCRAFT · © 2026</span>
        </div>
      </div>
    </div>
  );
}
